/**
 * High-level preferences hook
 * Combines store (IndexedDB) and sync (WebSocket) for easy usage
 * Automatically tracks changes and syncs to server
 */

import { useCallback, useEffect } from 'react';
import { usePreferenceStore } from './use-preference-store';
import { usePreferenceSync, type SyncOptions, type ConflictStrategy } from './use-preference-sync';
import { PreferencePayload } from '@maco144/shared';

export interface UsePreferencesOptions extends SyncOptions {
  userId?: string;
  wsUrl?: string; // WebSocket URL for server sync
}

/**
 * High-level hook for reading, writing, and syncing preferences
 * Handles both local storage (IndexedDB) and server sync
 */
export function usePreferences(options: UsePreferencesOptions = {}) {
  const { userId, wsUrl, ...syncOptions } = options;

  // Local storage
  const store = usePreferenceStore(userId);

  // Server sync
  const sync = usePreferenceSync(store, userId, wsUrl, syncOptions);

  // Get a preference (from cache)
  const getPreference = useCallback(
    async (key: string): Promise<unknown> => {
      const pref = await store.getPreference(key);
      return pref?.value;
    },
    [store]
  );

  // Set a preference (local + mark for sync)
  const setPreference = useCallback(
    async (key: string, value: unknown) => {
      // Save to local cache
      await store.setPreference(key, value);

      // Mark for sync to server
      sync.addPendingChange(key, value);
    },
    [store, sync]
  );

  // Set multiple preferences at once
  const setPreferences = useCallback(
    async (updates: Record<string, unknown>) => {
      // Save to local cache
      await store.setPreferences(updates);

      // Mark each for sync
      for (const [key, value] of Object.entries(updates)) {
        sync.addPendingChange(key, value);
      }
    },
    [store, sync]
  );

  // Delete a preference
  const deletePreference = useCallback(
    async (key: string) => {
      await store.deletePreference(key);
      sync.addPendingChange(key, undefined);
    },
    [store, sync]
  );

  // Manually trigger sync
  const sync_now = useCallback(async () => {
    await sync.syncToServer();
    await sync.syncFromServer();
  }, [sync]);

  // Force immediate sync on preference changes if desired
  useEffect(() => {
    // Could be used to immediately sync on write instead of waiting for interval
    // For now, let periodic sync handle it
  }, []);

  return {
    // Store state
    isReady: store.isInitialized,
    isLoading: store.isLoading,
    error: store.error,

    // Sync state
    isSyncing: sync.isSyncing,
    syncError: sync.syncError,
    lastSyncTime: sync.lastSyncTime,
    isPending: sync.isPending,
    hasPendingChanges: sync.hasPendingChanges,
    hasFailedChanges: sync.hasFailedChanges,
    failedChanges: sync.failedChanges,

    // Operations
    getPreference,
    setPreference,
    setPreferences,
    deletePreference,
    sync_now,
    exportAll: store.exportAll,
    importAll: store.importAll,
    clearAll: store.clearAll,
    retryFailed: sync.retryFailed,
  };
}

export type UsePreferencesReturn = ReturnType<typeof usePreferences>;

/**
 * Hook to get a single preference with auto-update on sync
 * Useful for components that only need one preference
 */
export function usePreference(
  key: string,
  options: UsePreferencesOptions = {}
): [unknown, (value: unknown) => Promise<void>, boolean] {
  const prefs = usePreferences(options);

  // For simplicity, return the preference value directly
  // In a real app, you'd want to use useState to track the current value
  // and update it when syncs complete
  const getValue = useCallback(async () => {
    return await prefs.getPreference(key);
  }, [key, prefs]);

  const setValue = useCallback(
    async (value: unknown) => {
      await prefs.setPreference(key, value);
    },
    [key, prefs]
  );

  return [undefined, setValue, prefs.isPending];
}
